/**
 * Phase 12.2b — Shared docs/wiki section identity map.
 *
 * Single source of truth for the (Icon, accent color) pair that
 * represents each doc section across the entire app:
 *
 *   • DocsLanding tile grid                    (already wired)
 *   • DocsLayout left-rail section headings    (this round)
 *   • DecryptedHeading H1 reveal color cycle   (this round)
 *   • Future: article-page H1, breadcrumb, search-result tag, RSS card,
 *     QorScore category badges, etc.
 *
 * The palette is chosen to telegraph each section's vibe at a glance
 * without needing to read the title:
 *
 *   Action          → green       (Get Started, Releases, Qelli)
 *   Investigative   → cyan        (Scanner, Atlas — "instruments")
 *   Audit/regulator → amber       (Audits, Compliance)
 *   Cryptography    → violet      (PQC, Methodology — "theory")
 *   Commercial      → gold        (Billing & Plans, Enterprise)
 *   Build/platform  → magenta     (Developers, Integrations)
 *   Org / privacy   → slate-blue  (Team & Access, Privacy & Data)
 *
 * Falls back to (BookOpen, cyan) for any unknown section title — same
 * behaviour as the original inline DocsLanding map.
 */
import {
  Rocket, BookOpen, Code2, Building2, ScrollText, Lock,
  Radar, ClipboardCheck, Map, Atom, Scale, CreditCard, Users, Plug,
  ShieldCheck, Sparkles, MoreHorizontal,
} from "lucide-react";


export const SECTION_STYLE_FALLBACK = {
  Icon:  BookOpen,
  color: "#00E5FF",
};

export const SECTION_STYLES = [
  // matched in order — first hit wins
  { keys: ["get started", "quickstart"],         Icon: Rocket,         color: "#1FDD83" },
  { keys: ["scanner"],                           Icon: Radar,          color: "#00E5FF" },
  { keys: ["audit"],                             Icon: ClipboardCheck, color: "#F5C156" },
  { keys: ["atlas"],                             Icon: Map,            color: "#00E5FF" },
  { keys: ["methodology"],                       Icon: BookOpen,       color: "#A78BFA" },
  { keys: ["post-quantum", "pqc", "quantum"],    Icon: Atom,           color: "#A78BFA" },
  { keys: ["compliance"],                        Icon: Scale,          color: "#F5C156" },
  { keys: ["security"],                          Icon: ShieldCheck,    color: "#F5806B" },
  { keys: ["billing", "plans"],                  Icon: CreditCard,     color: "#FFD66B" },
  { keys: ["team", "access"],                    Icon: Users,          color: "#8AB4D8" },
  { keys: ["integration", "webhook", "api key"], Icon: Plug,           color: "#FF6EA0" },
  { keys: ["privacy", "data retention"],         Icon: ShieldCheck,    color: "#8AB4D8" },
  { keys: ["developer", "sdk"],                  Icon: Code2,          color: "#FF6EA0" },
  { keys: ["enterprise"],                        Icon: Building2,      color: "#FFD66B" },
  { keys: ["qelli"],                             Icon: Sparkles,       color: "#1FDD83" },
  { keys: ["release", "changelog"],              Icon: ScrollText,     color: "#1FDD83" },
  { keys: ["internal"],                          Icon: Lock,           color: "#8AB4D8" },
  { keys: ["more"],                              Icon: MoreHorizontal, color: "#8AB4D8" },
];


/**
 * Look up the (Icon, color) pair for a section title.
 * Case-insensitive substring match against `keys` in declaration order.
 */
export function styleForSection(title) {
  const t = (title || "").toLowerCase();
  for (const cfg of SECTION_STYLES) {
    if (cfg.keys.some((k) => t.includes(k))) return cfg;
  }
  return SECTION_STYLE_FALLBACK;
}


/**
 * Just the colors, in declaration order — handy for animations that
 * cycle through "the docs contain ALL of these worlds" (e.g. the
 * DecryptedHeading H1 reveal).
 *
 * Deduped so the cycle doesn't visually repeat e.g. cyan twice in a
 * row (Scanner + Atlas both cyan).
 */
export const SECTION_COLORS_CYCLE = (() => {
  const seen = new Set();
  const out = [];
  for (const cfg of SECTION_STYLES) {
    if (!seen.has(cfg.color)) {
      seen.add(cfg.color);
      out.push(cfg.color);
    }
  }
  return out;
})();
